var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
var FollowersSparkline = function (_a) {
    var data = _a.data, _b = _a.height, height = _b === void 0 ? 60 : _b, _c = _a.color, color = _c === void 0 ? '#db2777' : _c, _d = _a.positiveColor, positiveColor = _d === void 0 ? '#10b981' : _d, _e = _a.negativeColor, negativeColor = _e === void 0 ? '#ef4444' : _e;
    // ✅ Estado para controlar hover, posição do tooltip e largura
    var _f = useState(null), hoveredIndex = _f[0], setHoveredIndex = _f[1];
    var _g = useState({ x: 0, y: 0 }), tooltipPos = _g[0], setTooltipPos = _g[1];
    var _h = useState(300), width = _h[0], setWidth = _h[1]; // Largura inicial padrão
    // ✅ Ref para o container
    var containerRef = useRef(null);
    // ✅ Atualizar largura dinamicamente ao montar e ao redimensionar
    useEffect(function () {
        var updateWidth = function () {
            if (containerRef.current) {
                setWidth(containerRef.current.offsetWidth);
            }
        };
        // Atualizar ao montar
        updateWidth();
        // Atualizar ao redimensionar janela
        window.addEventListener('resize', updateWidth);
        // Cleanup
        return function () { return window.removeEventListener('resize', updateWidth); };
    }, []);
    if (!data || data.length === 0) {
        return (React.createElement("div", { className: "flex items-center justify-center h-full text-gray-500 text-xs" }, "Sem dados"));
    }
    // Ordenar por data
    var sortedData = __spreadArray([], data, true).sort(function (a, b) {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
    // Calcular valores min/max para escala
    var followers = sortedData.map(function (d) { return d.followers; });
    var minFollowers = Math.min.apply(Math, followers);
    var maxFollowers = Math.max.apply(Math, followers);
    var range = maxFollowers - minFollowers || 1;
    // ✅ Padding ajustável
    var padding = { top: 8, bottom: 16, left: 8, right: 8 };
    var graphHeight = height - padding.top - padding.bottom;
    var graphWidth = width - padding.left - padding.right;
    // Calcular pontos do gráfico
    var points = sortedData.map(function (d, i) {
        var x = padding.left + (i / (sortedData.length - 1)) * graphWidth;
        var y = padding.top + graphHeight - ((d.followers - minFollowers) / range) * graphHeight;
        return __assign({ x: x, y: y }, d);
    });
    // Criar path da linha
    var linePath = points
        .map(function (p, i) { return "".concat(i === 0 ? 'M' : 'L', " ").concat(p.x, " ").concat(p.y); })
        .join(' ');
    // Criar path da área (opcional - gradiente)
    var areaPath = "\n    ".concat(linePath, "\n    L ").concat(points[points.length - 1].x, " ").concat(height - padding.bottom, "\n    L ").concat(padding.left, " ").concat(height - padding.bottom, "\n    Z\n  ");
    // Dia da semana em português
    var dayMap = {
        MON: 'SEG', TUE: 'TER', WED: 'QUA',
        THU: 'QUI', FRI: 'SEX', SAT: 'SAB', SUN: 'DOM'
    };
    // ✅ Formatar data completa (dd/mm/yyyy)
    var formatDate = function (dateStr) {
        var date = new Date(dateStr);
        return "".concat(date.getDate().toString().padStart(2, '0'), "/").concat((date.getMonth() + 1).toString().padStart(2, '0'), "/").concat(date.getFullYear());
    };
    // Último valor (hoje)
    var lastPoint = points[points.length - 1];
    var lastChange = lastPoint.followers_change;
    var changeColor = lastChange >= 0 ? positiveColor : negativeColor;
    var changeIcon = lastChange >= 0 ? '↑' : '↓';
    // ✅ Handlers para o tooltip
    var handleMouseEnter = function (index, e) {
        setHoveredIndex(index);
        setTooltipPos({ x: e.clientX, y: e.clientY });
    };
    var handleMouseMove = function (e) {
        setTooltipPos({ x: e.clientX, y: e.clientY });
    };
    var handleMouseLeave = function () {
        setHoveredIndex(null);
    };
    // ✅ Dados do ponto com hover
    var hoveredPoint = hoveredIndex !== null ? points[hoveredIndex] : null;
    return (React.createElement("div", { ref: containerRef, className: "flex flex-col items-center w-full relative" },
        React.createElement("svg", { width: "100%", height: height, className: "overflow-visible", viewBox: "0 0 ".concat(width, " ").concat(height), preserveAspectRatio: "none" },
            React.createElement("defs", null,
                React.createElement("linearGradient", { id: "sparkline-gradient", x1: "0%", y1: "0%", x2: "0%", y2: "100%" },
                    React.createElement("stop", { offset: "0%", stopColor: color, stopOpacity: "0.3" }),
                    React.createElement("stop", { offset: "100%", stopColor: color, stopOpacity: "0.05" }))),
            React.createElement("path", { d: areaPath, fill: "url(#sparkline-gradient)", stroke: "none" }),
            React.createElement("path", { d: linePath, fill: "none", stroke: color, strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", vectorEffect: "non-scaling-stroke" }),
            points.map(function (p, i) {
                var isLast = i === points.length - 1;
                var isHovered = hoveredIndex === i;
                return (React.createElement("g", { key: i },
                    React.createElement("circle", { cx: p.x, cy: p.y, r: 12, fill: "transparent", style: { cursor: 'pointer' }, onMouseEnter: function (e) { return handleMouseEnter(i, e); }, onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave, vectorEffect: "non-scaling-stroke" }),
                    React.createElement("circle", { cx: p.x, cy: p.y, r: isLast ? 5 : isHovered ? 5 : 3, fill: isLast ? color : isHovered ? color : '#fff', stroke: color, strokeWidth: isHovered ? 3 : 2, className: "transition-all pointer-events-none", style: {
                            filter: isHovered ? 'drop-shadow(0 0 4px rgba(219, 39, 119, 0.8))' : 'none'
                        }, vectorEffect: "non-scaling-stroke" }),
                    i % Math.ceil(points.length / 3) === 0 && (React.createElement("text", { x: p.x, y: height - 2, textAnchor: "middle", fontSize: "9", fill: "#9ca3af", fontWeight: "500", className: "pointer-events-none" }, dayMap[p.day] || p.day))));
            }),
            hoveredIndex === null && (React.createElement("text", { x: lastPoint.x, y: lastPoint.y - 12, textAnchor: "middle", fontSize: "11", fill: color, fontWeight: "700" }, lastPoint.followers))),
        React.createElement("div", { className: "flex items-center gap-2 mt-1" },
            React.createElement("span", { className: "text-xs font-bold flex items-center gap-0.5", style: { color: changeColor } },
                changeIcon,
                " ",
                Math.abs(lastChange)),
            React.createElement("span", { className: "text-xs text-gray-400" },
                "\u00FAltimos ",
                sortedData.length,
                " dias")),
        hoveredPoint &&
            ReactDOM.createPortal((function () {
                // Ajuste dinâmico para não sair da tela
                var tooltipWidth = 280;
                var tooltipHeight = 200;
                var padding = 24;
                var winW = window.innerWidth;
                var winH = window.innerHeight;
                var left = tooltipPos.x + 16;
                var top = tooltipPos.y + 16;
                if (left + tooltipWidth + padding > winW) {
                    left = winW - tooltipWidth - padding;
                }
                if (top + tooltipHeight + padding > winH) {
                    top = winH - tooltipHeight - padding;
                }
                if (left < padding)
                    left = padding;
                if (top < padding)
                    top = padding;
                var pointChangeColor = hoveredPoint.followers_change >= 0 ? positiveColor : negativeColor;
                var pointChangeIcon = hoveredPoint.followers_change >= 0 ? '↑' : '↓';
                return (React.createElement("div", { style: {
                        position: 'fixed',
                        left: left,
                        top: top,
                        zIndex: 40,
                        pointerEvents: 'none',
                    } },
                    React.createElement("div", { className: "rounded-lg shadow-xl bg-gray-900/95 border-2 px-4 py-3", style: {
                            minWidth: 220,
                            maxWidth: 280,
                            color: "#f3f3f3",
                            fontFamily: "Inter, sans-serif",
                            borderColor: pointChangeColor,
                        } },
                        React.createElement("div", { className: "mb-3 text-center" },
                            React.createElement("div", { className: "font-bold text-pink-400 text-lg leading-tight" }, formatDate(hoveredPoint.date)),
                            React.createElement("div", { className: "text-xs text-gray-400 font-semibold" }, dayMap[hoveredPoint.day] || hoveredPoint.day)),
                        React.createElement("div", { className: "flex items-center justify-center gap-2 mb-2" },
                            React.createElement("span", { className: "text-2xl" }, "\uD83D\uDC65"),
                            React.createElement("div", { className: "flex flex-col" },
                                React.createElement("span", { className: "text-xs text-gray-300 font-semibold" }, "Seguidores"),
                                React.createElement("span", { className: "text-2xl font-bold text-white" }, hoveredPoint.followers.toLocaleString('pt-BR')))),
                        React.createElement("div", { className: "flex items-center justify-center gap-2 mb-3 py-2 rounded", style: { backgroundColor: 'rgba(0,0,0,0.2)' } },
                            React.createElement("span", { className: "text-xl" }, pointChangeIcon),
                            React.createElement("div", { className: "flex flex-col" },
                                React.createElement("span", { className: "text-xs text-gray-300" }, "Varia\u00E7\u00E3o"),
                                React.createElement("span", { className: "text-xl font-bold", style: { color: pointChangeColor } },
                                    hoveredPoint.followers_change >= 0 ? '+' : '',
                                    hoveredPoint.followers_change))),
                        React.createElement("div", { className: "flex flex-col gap-2 text-sm border-t border-gray-700 pt-2" },
                            React.createElement("div", { className: "flex items-center justify-between" },
                                React.createElement("div", { className: "flex items-center gap-2" },
                                    React.createElement("span", { className: "text-base" }, "\uD83D\uDDBC\uFE0F"),
                                    React.createElement("span", { className: "font-semibold text-gray-300" }, "Visitas ao perfil")),
                                React.createElement("span", { className: "font-bold text-white" }, hoveredPoint.profile_views)),
                            React.createElement("div", { className: "flex items-center justify-between" },
                                React.createElement("div", { className: "flex items-center gap-2" },
                                    React.createElement("span", { className: "text-base" }, "\uD83D\uDCCA"),
                                    React.createElement("span", { className: "font-semibold text-gray-300" }, "Taxa de convers\u00E3o")),
                                React.createElement("span", { className: "font-bold text-white" },
                                    hoveredPoint.conversion_rate.toFixed(1),
                                    "%")),
                            hoveredPoint.profile_views > 0 && (React.createElement("div", { className: "text-xs text-gray-400 text-center mt-1 italic" }, hoveredPoint.profile_views > 0
                                ? "".concat(hoveredPoint.profile_views, " visitas geraram ").concat(Math.abs(hoveredPoint.followers_change), " ").concat(hoveredPoint.followers_change >= 0 ? 'novos seguidores' : 'perdas')
                                : 'Sem visitas registradas'))))));
            })(), document.body)));
};
export default FollowersSparkline;
